import {Banner, Button, Link as PrimerLink} from '@primer/react'
import {CodeReviewIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import type {ReviewRequestBanner as ReviewRequestBannerData} from '../../page-data/payloads/header'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Link} from '@github-ui/react-core/link'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import styles from './ReviewRequestBanner.module.css'

export function ReviewRequestBanner({
  reviewRequest,
  urls,
}: {
  reviewRequest: ReviewRequestBannerData
  urls: NavigationUrls
}) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isChangesEnabled = useFeatureFlag('react_data_router_pull_request_files')
  const url = isChangesEnabled ? `${urls.changes}` : `${urls.files}`
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')

  if (!reviewRequest.render) return null

  const message =
    reviewRequest.requestedBySelf || !reviewRequest.requestingUserDisplayLogin ? (
      'This pull request is waiting on your review.'
    ) : (
      <>
        <PrimerLink href={`/${reviewRequest.requestingUserDisplayLogin}`} className="Link--primary text-bold">
          {reviewRequest.requestingUserDisplayLogin}
        </PrimerLink>{' '}
        requested your review on this pull request.
      </>
    )

  return (
    <Banner
      /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
      aria-label="Review Request Banner"
      className={clsx('flex-row width-full', showUpdatedHeader && styles.warningOverride)}
      variant={showUpdatedHeader ? 'info' : 'warning'}
      title="Review requested"
      layout="compact"
      hideTitle
      leadingVisual={showUpdatedHeader ? <CodeReviewIcon /> : undefined}
      primaryAction={
        showUpdatedHeader ? undefined : (
          <Button as={Link} variant="primary" size="small" to={url} className="mr-1">
            Add your review
          </Button>
        )
      }
    >
      <Banner.Description>{message}</Banner.Description>
    </Banner>
  )
}
